import React, { Component, ErrorInfo, ReactNode } from 'react';
import { cleanupServiceWorkersAndCaches } from '@/utils/service-worker-cleanup';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/** Detecta error de chunk obsoleto (tras un nuevo deploy). */
function isChunkLoadError(error: Error): boolean {
  const msg = error?.message ?? '';
  return (
    msg.includes('ChunkLoadError') ||
    msg.includes('Loading chunk') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('dynamically imported module')
  );
}

/** Errores de contexto/providers: se muestran como 404 amigable. */
function isProviderContextError(error: Error | null): boolean {
  const msg = error?.message ?? '';
  return (
    msg.includes('useAuth must be used within') ||
    msg.includes('must be used within an AuthProvider') ||
    msg.includes('must be used within a') ||
    msg.includes('must be used within')
  );
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error capturado por ErrorBoundary:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.assign('/eventos');
  };

  /** Limpia caché y service workers y recarga para cargar la versión nueva (tras deploy). */
  handleReloadAfterDeploy = async () => {
    await cleanupServiceWorkersAndCaches();
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isChunk = this.state.error && isChunkLoadError(this.state.error);
      const showAs404 = !isChunk && isProviderContextError(this.state.error);

      if (showAs404) {
        return (
          <div className="not-found-page" role="alert">
            <p className="not-found-code">404</p>
            <h1 className="not-found-title">Página no encontrada</h1>
            <p className="not-found-message">
              No pudimos cargar esta vista. Vuelve al inicio o recarga la aplicación.
            </p>
            <div className="not-found-actions">
              <button
                type="button"
                className="not-found-btn not-found-btn--primary"
                onClick={this.handleGoHome}
              >
                Ir a eventos
              </button>
              <button
                type="button"
                className="not-found-btn not-found-btn--secondary"
                onClick={this.handleReset}
              >
                Recargar aplicación
              </button>
            </div>
          </div>
        );
      }

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            padding: '20px',
            textAlign: 'center',
            backgroundColor: 'var(--bg-0)',
          }}
        >
          <h1 style={{ color: 'var(--text-1, #ff3b30)', marginBottom: '16px' }}>
            {isChunk ? 'Nueva versión disponible' : 'Algo salió mal'}
          </h1>
          <p style={{ color: 'var(--text-2, #666)', marginBottom: '24px', maxWidth: '500px' }}>
            {isChunk
              ? 'La aplicación se ha actualizado. Pulsa el botón para cargar la última versión.'
              : (this.state.error?.message || 'Ocurrió un error inesperado')}
          </p>
          <button
            onClick={isChunk ? this.handleReloadAfterDeploy : this.handleReset}
            style={{
              padding: '12px 24px',
              backgroundColor: 'var(--accent)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
            }}
          >
            {isChunk ? 'Actualizar aplicación' : 'Recargar aplicación'}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
